import {
  getInvestorSubmitErrorCode,
  getInvestorSubmitErrorMessage,
  getInvestorSubmitErrorStatus,
  getInvestorSubmitResponseRequestId,
  hashText,
  sanitizeFingerprintPart,
} from '@/common/components/investor-profile-form/investor-submit-error-utils';

export type DeleteInvestorFailureKind =
  | 'access_denied'
  | 'already_deleted'
  | 'invalid_request'
  | 'signed_up_conflict'
  | 'unconfirmed';

type DeleteInvestorSupportContext = {
  error: unknown;
  investorDisplayName: string;
  investorId: string;
  kind: DeleteInvestorFailureKind;
  retryAttempted: boolean;
};

const normalizeDeleteInvestorErrorText = (error: unknown): string =>
  [getInvestorSubmitErrorCode(error), getInvestorSubmitErrorMessage(error)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const getDeleteInvestorFailureKind = (error: unknown): DeleteInvestorFailureKind => {
  const status = getInvestorSubmitErrorStatus(error);
  const normalized = normalizeDeleteInvestorErrorText(error);

  if (status === 404) {
    return 'already_deleted';
  }
  if (status === 401 || status === 403) {
    return 'access_denied';
  }
  if (
    normalized.includes('signed_up_investor_delete_blocked') ||
    normalized.includes('already signed') ||
    normalized.includes('active account') ||
    normalized.includes('cannot delete') ||
    normalized.includes("can't delete")
  ) {
    return 'signed_up_conflict';
  }
  if (status === 400 || status === 409 || status === 422) {
    return 'invalid_request';
  }

  return 'unconfirmed';
};

export const canRetryDeleteInvestorFailure = (kind: DeleteInvestorFailureKind): boolean =>
  kind === 'unconfirmed';

export const canRefreshListForDeleteInvestorFailure = (kind: DeleteInvestorFailureKind): boolean =>
  kind === 'invalid_request';

export const canContactTeamForDeleteInvestorFailure = ({
  kind,
  retryAttempted,
}: Pick<DeleteInvestorSupportContext, 'kind' | 'retryAttempted'>): boolean =>
  retryAttempted && kind === 'unconfirmed';

export const normalizeDeleteInvestorErrorFingerprint = (error: unknown): string => {
  const kind = getDeleteInvestorFailureKind(error);
  if (kind !== 'unconfirmed') {
    return `advisor_delete_investor:${kind}`;
  }

  const status = getInvestorSubmitErrorStatus(error);
  if (status !== null) {
    return `advisor_delete_investor:http_${status}`;
  }

  const errorCode = getInvestorSubmitErrorCode(error);
  if (errorCode) {
    return `advisor_delete_investor:${sanitizeFingerprintPart(errorCode)}`;
  }

  const message = getInvestorSubmitErrorMessage(error);
  if (message) {
    return `advisor_delete_investor:unknown_${hashText(message)}`;
  }

  return 'advisor_delete_investor:unknown';
};

export const buildDeleteInvestorSupportContext = ({
  error,
  investorDisplayName,
  investorId,
  kind,
  retryAttempted,
}: DeleteInvestorSupportContext): Record<string, unknown> => ({
  advisor_blocked: true,
  error_code: getInvestorSubmitErrorCode(error),
  error_fingerprint: normalizeDeleteInvestorErrorFingerprint(error),
  error_kind: kind,
  error_request_id: getInvestorSubmitResponseRequestId(error),
  error_status: getInvestorSubmitErrorStatus(error),
  feature: 'advisor_delete_investor',
  flow: 'advisor_delete_investor',
  investor_display_fingerprint: hashText(investorDisplayName),
  investor_id_fingerprint: hashText(investorId),
  retry_attempted: retryAttempted,
  severity: 'blocking',
  source: 'advisor_contact_team',
  stage: 'delete_investor_submit',
  support_triggered: true,
});

export const buildDeleteInvestorSupportError = (error: unknown): Error =>
  new Error(`Advisor delete investor failed: ${normalizeDeleteInvestorErrorFingerprint(error)}`);
